import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Send, 
  Loader2, 
  AlertCircle,
  Copy,
  CheckCircle,
  BarChart,
  Search,
  Zap,
  Lightbulb,
  ThumbsUp,
  ThumbsDown,
  ArrowUpRight,
  Sparkles
} from 'lucide-react';
import { useCaptionAnalysis } from '../../lib/hooks/useCaptionAnalysis';

const SEOCaptionAnalyzer = () => {
  const [caption, setCaption] = useState('');
  const [platform, setPlatform] = useState('youtube');
  const [copied, setCopied] = useState<string | null>(null);
  const { result: analysisResult, loading, error, analyzeCaption } = useCaptionAnalysis();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await analyzeCaption(caption, platform);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getScoreBackground = (score: number) => {
    if (score >= 80) return 'bg-green-100 dark:bg-green-900/20';
    if (score >= 60) return 'bg-yellow-100 dark:bg-yellow-900/20';
    return 'bg-red-100 dark:bg-red-900/20';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Needs Improvement';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-8"
    >
      <div className="flex items-center space-x-4">
        <Link to="/ai-tools" className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">SEO Caption Analyzer</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Optimize your captions for better searchability and reach
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center space-x-2">
          <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400" />
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      <div className="bg-white dark:bg-dark-800 rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Analyze Your Caption</h2>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="platform" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Platform
            </label>
            <select
              id="platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            >
              <option value="youtube">YouTube</option>
              <option value="tiktok">TikTok</option>
              <option value="instagram">Instagram</option>
              <option value="facebook">Facebook</option>
              <option value="twitter">Twitter</option>
            </select>
          </div>

          <div>
            <label htmlFor="caption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Caption or Description
            </label>
            <textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={6}
              placeholder="Paste your caption or description here to analyze its SEO effectiveness..."
              className="block w-full rounded-lg border-gray-300 dark:border-dark-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-dark-700 dark:text-white sm:text-sm"
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading || !caption.trim()}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                loading || !caption.trim()
                  ? 'bg-gray-300 dark:bg-dark-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700'
              }`}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Analyzing...</span>
                </>
              ) : (
                <>
                  <Search className="h-5 w-5" />
                  <span>Analyze Caption</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {analysisResult && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center">
              <BarChart className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
              Analysis Results
            </h2>
            
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Platform: <span className="font-medium">{analysisResult.platform.charAt(0).toUpperCase() + analysisResult.platform.slice(1)}</span>
              </span>
            </div>
          </div>

          {/* Score Card */}
          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">SEO Score</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Based on searchability and engagement potential
                </p>
              </div>
              
              <div className="flex items-center">
                <div className={`w-24 h-24 rounded-full flex items-center justify-center ${getScoreBackground(analysisResult.score.total)}`}>
                  <span className={`text-3xl font-bold ${getScoreColor(analysisResult.score.total)}`}>
                    {analysisResult.score.total}
                  </span>
                </div>
                <div className="ml-4">
                  <span className={`text-lg font-semibold ${getScoreColor(analysisResult.score.total)}`}>
                    {getScoreLabel(analysisResult.score.total)}
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {analysisResult.score.total >= 80 
                      ? 'Great job! Your caption is well-optimized.'
                      : analysisResult.score.total >= 60
                      ? 'Good start, but there\'s room for improvement.'
                      : 'Your caption needs significant optimization.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* No Connected Accounts Message */}
          {!analysisResult.score.total && (
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg p-4 mt-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                    No connected accounts
                  </p>
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                    Connect your social media accounts in Settings to get personalized analysis based on your actual performance metrics.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Strengths and Weaknesses */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Strengths */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="flex items-center space-x-2 mb-4">
                <ThumbsUp className="h-5 w-5 text-green-600 dark:text-green-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Strengths</h3>
              </div>
              <ul className="space-y-2">
                {analysisResult.strengths.map((strength, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-green-600 dark:text-green-400 mr-2">•</span>
                    <span className="text-gray-700 dark:text-gray-300">{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Weaknesses */}
            <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="flex items-center space-x-2 mb-4">
                <ThumbsDown className="h-5 w-5 text-red-600 dark:text-red-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Areas to Improve</h3>
              </div>
              <ul className="space-y-2">
                {analysisResult.weaknesses.map((weakness, index) => (
                  <li key={index} className="flex items-start">
                    <span className="text-red-600 dark:text-red-400 mr-2">•</span>
                    <span className="text-gray-700 dark:text-gray-300">{weakness}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Keywords */}
          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <BarChart className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Score Breakdown</h3>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Engagement</p>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult.score.breakdown.engagement}%
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-600 rounded-full mt-1">
                    <div 
                      className="h-2 bg-indigo-600 rounded-full" 
                      style={{ width: `${analysisResult.score.breakdown.engagement}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Reach</p>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult.score.breakdown.reach}%
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-600 rounded-full mt-1">
                    <div 
                      className="h-2 bg-indigo-600 rounded-full" 
                      style={{ width: `${analysisResult.score.breakdown.reach}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Growth</p>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult.score.breakdown.growth}%
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-600 rounded-full mt-1">
                    <div 
                      className="h-2 bg-indigo-600 rounded-full" 
                      style={{ width: `${analysisResult.score.breakdown.growth}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Keywords</p>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult.score.breakdown.keywords}%
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-600 rounded-full mt-1">
                    <div 
                      className="h-2 bg-indigo-600 rounded-full" 
                      style={{ width: `${analysisResult.score.breakdown.keywords}%` }}
                    ></div>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-gray-50 dark:bg-dark-700 rounded-lg text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Consistency</p>
                <div className="text-lg font-semibold text-gray-900 dark:text-white">
                  {analysisResult.score.breakdown.consistency}%
                  <div className="w-full h-2 bg-gray-200 dark:bg-dark-600 rounded-full mt-1">
                    <div 
                      className="h-2 bg-indigo-600 rounded-full" 
                      style={{ width: `${analysisResult.score.breakdown.consistency}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Keywords */}
          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center space-x-2 mb-4">
              <Search className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Key Search Terms</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              {analysisResult.keywords.map((keyword, index) => (
                <span 
                  key={index}
                  className="px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300 rounded-lg text-sm"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center space-x-2 mb-4">
              <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recommendations</h3>
            </div>
            <ul className="space-y-3">
              {/* Remove duplicates and limit to 5 recommendations */}
              {[...new Set([...analysisResult.recommendations, ...analysisResult.score.recommendations])].slice(0, 5).map((recommendation, index) => (
                <li key={index} className="flex items-start p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg">
                  <Zap className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2 flex-shrink-0 mt-0.5" />
                  <span className="text-amber-800 dark:text-amber-200">{recommendation}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Improved Versions */}
          <div className="bg-white dark:bg-dark-800 rounded-xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Improved Versions</h3>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {analysisResult.improvedVersions.length > 0 ? 'Click to copy' : 'No improved versions available'}
              </span>
            </div>
            <div className="space-y-4">
              {analysisResult.improvedVersions.map((version, index) => (
                <div 
                  key={index}
                  className="p-4 bg-indigo-50 dark:bg-indigo-900/10 rounded-lg relative group cursor-pointer"
                  onClick={() => handleCopy(version)}
                >
                  <p className="text-gray-800 dark:text-gray-200 pr-8">{version}</p>
                  <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                    {copied === version ? (
                      <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                    ) : (
                      <Copy className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Platform-specific tips */}
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-6 text-white">
            <div className="flex items-center space-x-3 mb-4">
              <ArrowUpRight className="h-5 w-5" />
              <h3 className="text-lg font-semibold">{analysisResult.platform.charAt(0).toUpperCase() + analysisResult.platform.slice(1)} SEO Tips</h3>
            </div>
            <ul className="space-y-2 text-indigo-100">
              {analysisResult.platform === 'youtube' && (
                <>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Include primary keywords in the first 1-2 sentences</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Aim for descriptions that are at least 250 words</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Add timestamps for longer videos</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Include 3-5 relevant hashtags at the end</span>
                  </li>
                </>
              )}
              {analysisResult.platform === 'facebook' && (
              <>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Keep captions under 80 characters for optimal engagement</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Use 1-2 relevant hashtags maximum</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Include a clear call-to-action</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Post when your audience is most active</span>
                </li>
              </>
              )}
              {analysisResult.platform === 'tiktok' && (
                <>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Keep captions concise (150 characters or less)</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Use 3-5 trending hashtags</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Include a call-to-action</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Use emojis strategically</span>
                  </li>
                </>
              )}
              {analysisResult.platform === 'instagram' && (
                <>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Front-load important keywords</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Use 20-30 relevant hashtags</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Include a question to boost engagement</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="text-white">•</span>
                    <span>Add line breaks for readability</span>
                  </li>
                </>
              )}
              {analysisResult.platform === 'twitter' && (
                <>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Keep tweets under 280 characters (ideally 71-100)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Use 1-2 relevant hashtags for better reach</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Include mentions (@username) to engage others</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-white">•</span>
                  <span>Use emojis sparingly for emphasis</span>
                </li>
                </>
              )}
            </ul>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default SEOCaptionAnalyzer;